import { WORLD } from '../../core/config';
import { layoutToWorld } from '../../core/coords';
import { fromRing, intersection } from '../../geo/clip';
import { ensureCCW } from '../../geo/polygon';
import type { Vec2 } from '../../geo/types';
import { bridgeProfile } from '../../site/bridgeHeight';
import type { BridgeDeck } from '../../site/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { stationsOf, type ArchBridgeSpec } from './archBridge';
import { ARCH_DRESSING_PROJECTION, buildArchDressings } from './archDressings';
import { buildBridgeSolid } from './bridgeSolid';
import { buildParapets } from './bridgeParapets';
import { estimatedVaults } from './vaultEstimate';

const MOL_BRIDGE = 'bridge-molsteeg-torensteeg';
const ABUTMENT_M = 0.9;
const CROWN_COVER_M = 0.75;
// The arch stones stand proud of the vault face without widening the recorded crossing.
const VAULT_RECESS_M = 0.04;
const SLAB = { depth: 0.3, overlap: 0.15 } as const;
const SIDE_TOLERANCE_M = 0.01;

/** Estimated masonry vaults at the documented Molsteeg–Torensteeg crossing.
 * The full bank-to-bank frame keeps the skew landings level and the middle portal open. */
export function molBridgeSpec(bridge: BridgeDeck): ArchBridgeSpec {
  const p = bridgeProfile(bridge);
  const xs = bridge.deck.flatMap((d) => d.outer.map(p.along));
  const middle = (p.from + p.to) / 2;
  const left = Math.max(...xs.filter((x) => x < middle)) + ABUTMENT_M;
  const right = Math.min(...xs.filter((x) => x > middle)) - ABUTMENT_M;
  const vaults = estimatedVaults(right - left);
  // A vault under a ramp crowns lower than the one under the crest.
  const crowns = vaults.crownsAt.map((x) => p.height(left + x) - CROWN_COVER_M);
  return {
    length: p.to - p.from,
    width: bridge.width,
    spans: crowns.map(() => vaults.span),
    firstArchAt: left - p.from,
    pierWidth: vaults.pierWidth,
    springY: WORLD.waterY - 0.1,
    crownY: p.height(middle) - CROWN_COVER_M,
    crowns,
    footY: -2.5,
    parapet: { height: 0, thickness: 0 },
    deckY: (x) => p.height(p.from + x),
  };
}

export function buildMolBridge(
  builder: MeshBuilder,
  stone: VertexStyle,
  bridge: BridgeDeck
): boolean {
  if (bridge.id !== MOL_BRIDGE) return false;
  const p = bridgeProfile(bridge);
  const at = (x: number, across: number): Vec2 => [
    bridge.a[0] + p.axis[0] * (p.from + x) - p.axis[1] * across,
    bridge.a[1] + p.axis[1] * (p.from + x) + p.axis[0] * across,
  ];
  const band = (from: number, to: number, halfWidth: number) =>
    fromRing([at(from, -halfWidth), at(to, -halfWidth), at(to, halfWidth), at(from, halfWidth)]);
  const frame: Frame = {
    origin: layoutToWorld(at(0, 0), 0),
    yaw: Math.atan2(p.axis[1], p.axis[0]),
  };
  const spec = molBridgeSpec(bridge);
  const stations = stationsOf(spec);
  const cuts = [...new Set([...stations.map((s) => s.x), ...p.knots.map((x) => x - p.from)])]
    .filter((x) => x >= 0 && x <= spec.length)
    .sort((a, b) => a - b);
  // Each vault column keeps the actual skew bank cut. The slab overlaps the vault,
  // avoiding coincident internal caps while giving the walking surface its full width.
  for (let k = 1; k < cuts.length; k++) {
    const from = cuts[k - 1]!,
      to = cuts[k]!;
    if (to - from < 1e-6) continue;
    const middle = (from + to) / 2;
    const index = stations.findIndex((s) => s.x > middle);
    const a = stations[index - 1]!,
      b = stations[index]!;
    const bottom = (point: Vec2) => {
      const t = Math.max(0, Math.min(1, (p.along(point) - p.from - a.x) / (b.x - a.x)));
      return a.after + (b.before - a.after) * t;
    };
    for (const piece of intersection(bridge.deck, [
      band(from, to, bridge.width / 2 - VAULT_RECESS_M),
    ]))
      buildBridgeSolid(
        builder,
        stone,
        piece,
        bottom,
        (point) => p.height(p.along(point)) - SLAB.overlap
      );
    for (const piece of intersection(bridge.deck, [band(from, to, bridge.width)]))
      buildBridgeSolid(
        builder,
        stone,
        piece,
        (point) => p.height(p.along(point)) - SLAB.depth,
        (point) => p.height(p.along(point))
      );
  }
  buildArchDressings(builder, stone, frame, {
    ...spec,
    width: bridge.width - 2 * ARCH_DRESSING_PROJECTION,
  });
  const across = (point: Vec2) =>
    -(point[0] - bridge.a[0]) * p.axis[1] + (point[1] - bridge.a[1]) * p.axis[0];
  const sides = bridge.deck.flatMap((deck) => {
    const ring = ensureCCW(deck.outer);
    return ring.flatMap((a, i) => {
      const b = ring[(i + 1) % ring.length]!,
        ca = across(a),
        cb = across(b);
      return Math.abs(Math.abs(ca) - bridge.width / 2) <= SIDE_TOLERANCE_M &&
        Math.abs(Math.abs(cb) - bridge.width / 2) <= SIDE_TOLERANCE_M &&
        Math.sign(ca) === Math.sign(cb)
        ? [{ a, b }]
        : [];
    });
  });
  buildParapets(builder, stone, sides, p);
  return true;
}
